import { useState, useEffect } from 'react';
import { Modal, Button, Form, Input, Select, Radio, DatePicker, message } from 'antd';
import dayjs from 'dayjs';
import './style.less';
import { saveGuestJourney } from '@services/Guest';
import WorkExperiences from '@/components/common/WorkExperiences';
import RemarksTip from '../RemarksTip';
import { logChangeHotelInfo } from '@/utils/conferenceLogger';

const { TextArea } = Input;
const { Option } = Select;

// 工具函数：生成日期范围内的所有日期
const generateDateRange = (checkinDate, checkoutDate) => {
    const dates = [];
    const checkinStr = checkinDate.format('YYYY-MM-DD');
    const checkoutStr = checkoutDate.format('YYYY-MM-DD');

    let currentDate = dayjs(checkinStr);
    while (currentDate.isBefore(checkoutStr)) {
        dates.push(currentDate.format('YYYY-MM-DD'));
        currentDate = currentDate.add(1, 'day');
    }

    return dates;
};

// 工具函数：将房型数据转换为选项格式
const convertRoomTypesToOptions = (roomTypes, hotel, checkinDate, checkoutDate) => {
    // 生成需要检查的日期范围（不包含退房日期）
    const dates = generateDateRange(checkinDate, checkoutDate);

    return roomTypes.map(rt => {
        // 检查该房型在所有日期是否都有余量
        const hasAvailableRooms = dates.every(date => {
            const dayData = hotel.roomTypeDays?.find(d => d.day === date);
            if (!dayData || !dayData.roomInfo) {
                return false;
            }

            // 查找该房型在当前日期的房间信息
            const roomData = dayData.roomInfo.find(r => r.roomTypeId === rt.id);
            if (!roomData) {
                return false;
            }

            // 检查是否有余量：roomNum - roomUsage > 0
            const available = roomData.roomNum - roomData.roomUsage > 0;
            return available;
        });

        return {
            value: rt.id,
            label: rt.roomType,
            disabled: !hasAvailableRooms,
        };
    });
};

// 工具函数：重置所有状态
const createResetState = () => ({
    hotelRoomTypeOptions: [],
    selectedHotelId: null,
    filteredHotelOptions: [],
    selectedDateRange: { checkin: null, checkout: null },
});

// 表单值类型
const HotelEditModal = ({
    open,
    initialData,
    guestInfo,
    hotelOptions = [], // 酒店选项
    conferenceId,
    expertId,
    onCancel,
    onSubmit,
}) => {
    const [form] = Form.useForm();
    const [needHotel, setNeedHotel] = useState(1); // 1: 会务组安排, 2: 自费, 0: 不需要
    const [selectedHotelId, setSelectedHotelId] = useState(null);
    const [hotelRoomTypeOptions, setHotelRoomTypeOptions] = useState([]);

    // 根据日期筛选后的酒店和房型选项
    const [filteredHotelOptions, setFilteredHotelOptions] = useState([]);
    const [selectedDateRange, setSelectedDateRange] = useState({ checkin: null, checkout: null });

    // 初始化房型选项
    const initializeRoomTypeOptions = (hotelId, checkInDate, checkOutDate) => {
        if (!hotelId) {
            setSelectedHotelId(null);
            setHotelRoomTypeOptions([]);
            return;
        }

        setSelectedHotelId(hotelId);
        const selectedHotel = hotelOptions.find(h => h.value === hotelId);

        if (selectedHotel && selectedHotel.roomTypes) {
            const roomTypesToSet = convertRoomTypesToOptions(selectedHotel.roomTypes, selectedHotel, checkInDate, checkOutDate);
            setHotelRoomTypeOptions(roomTypesToSet);
        } else {
            setHotelRoomTypeOptions([]);
        }
    };

    // 重置所有状态
    const resetAllStates = () => {
        const resetState = createResetState();
        setHotelRoomTypeOptions(resetState.hotelRoomTypeOptions);
        setSelectedHotelId(resetState.selectedHotelId);
        setFilteredHotelOptions(resetState.filteredHotelOptions);
        setSelectedDateRange(resetState.selectedDateRange);
    };

    // 初始化表单数据
    useEffect(() => {
        if (open && initialData) {
            // 回显原住宿信息
            const checkInDate = initialData.hotel?.checkin_date ? dayjs(initialData.hotel.checkin_date) : null;
            const checkOutDate = initialData.hotel?.checkout_date ? dayjs(initialData.hotel.checkout_date) : null;

            // 判断是否需要住宿
            let needValue = null; // 默认会务组安排
            if (initialData.need === false || initialData.need === 0) {
                needValue = 0; // 不需要
            }
            if (initialData.need === 2) {
                needValue = 2; // 自费
            }
            if (initialData.need === 1) {
                needValue = 1; // 会务组安排
            }

            const hotelId = initialData.hotel?.hotel_id;
            const roomTypeId = initialData.hotel?.hotel_room_type_id;

            setNeedHotel(needValue);

            // 设置日期范围
            if (checkInDate && checkOutDate) {
                setSelectedDateRange({ checkin: checkInDate, checkout: checkOutDate });
                filterHotelsByDateRange(checkInDate, checkOutDate);
            } else {
                setSelectedDateRange({ checkin: null, checkout: null });
                setFilteredHotelOptions(hotelOptions);
            }

            // 初始化房型选项
            initializeRoomTypeOptions(hotelId, checkInDate, checkOutDate);

            // 设置表单值
            form.setFieldsValue({
                need: needValue,
                checkin_date: checkInDate,
                checkout_date: checkOutDate,
                hotel_id: hotelId,
                hotel_room_type_id: roomTypeId,
                remarks: initialData.remarks || '',
                room_no: initialData.hotel?.room_no || '',
            });
        } else if (open && !initialData) {
            // 不设置默认值
            form.setFieldsValue({
                need: null,
                checkin_date: null,
                checkout_date: null,
                hotel_id: undefined,
                hotel_room_type_id: undefined,
                remarks: '',
                room_no: '',
            });

            setNeedHotel(null);
            setSelectedHotelId(null);
            setHotelRoomTypeOptions([]);
            setFilteredHotelOptions(hotelOptions);
            setSelectedDateRange({ checkin: null, checkout: null });
        } else if (!open) {
            // 弹窗关闭时重置状态
            resetAllStates();
        }
    }, [open, initialData, hotelOptions]);

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        resetAllStates();
        onCancel();
    };

    // 处理提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            console.log();

            let formData;

            // 如果选择无需住宿，则只传递 need 标志
            if (values.need === 0) {
                formData = {
                    ...initialData, // 保留原住宿信息
                    need: 0,
                    remarks: values.remarks,
                };
            } else {
                if (values.checkin_date && values.checkout_date && values.checkin_date.isAfter(values.checkout_date)) {
                    message.error('入住日期不能晚于退房日期');
                    return;
                }

                formData = {
                    need: values.need, // 1: 会务组安排, 2: 自费
                    remarks: values.remarks,
                    hotel: {
                        checkin_date: values.checkin_date ? values.checkin_date.format('YYYY-MM-DD') : null,
                        checkout_date: values.checkout_date ? values.checkout_date.format('YYYY-MM-DD') : null,
                        hotel_id: values.hotel_id,
                        hotel_room_type_id: values.hotel_room_type_id,
                        room_no: values.room_no,
                    },
                };
            }

            // 调用保存接口
            const dataWithTimestamp = {
                ...formData,
                updated_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            };

            const params = {
                conferenceId,
                expertId,
                hotelInfo: JSON.stringify(dataWithTimestamp),
            };

            const res = await saveGuestJourney(params);

            if (res.status_code === 200) {
                message.success('保存成功');
                form.resetFields();
                onSubmit && onSubmit(formData);

                // 上报操作日志
                // 只有在变更酒店住宿信息时才上报（存在 initialData 且变更了酒店住宿信息）
                if (initialData && formData.hotel && formData.need !== 0) {
                    try {
                        // 从 hotelOptions 中获取酒店名称和房型名称
                        const selectedHotel = hotelOptions.find(h => h.value === formData.hotel.hotel_id);
                        const hotelName = selectedHotel?.label || '';

                        let roomTypeName = '';
                        if (selectedHotel && selectedHotel.roomTypes && formData.hotel.hotel_room_type_id) {
                            const selectedRoomType = selectedHotel.roomTypes.find(rt => rt.id === formData.hotel.hotel_room_type_id);
                            roomTypeName = selectedRoomType?.roomType || '';
                        }

                        const hotelInfo = {
                            checkin_date: formData.hotel.checkin_date || '',
                            checkout_date: formData.hotel.checkout_date || '',
                            hotel_id: formData.hotel.hotel_id || null,
                            hotel: hotelName,
                            hotel_room_type_id: formData.hotel.hotel_room_type_id || null,
                            hotel_room_type: roomTypeName,
                            room_no: formData.hotel.room_no || '',
                        };

                        const logOptions = {
                            conferenceId,
                            expertId,
                            expertName: guestInfo?.name || '',
                            hotelInfo,
                            reason: '', // 酒店住宿变更没有单独的变更理由字段
                            isSuccess: true,
                        };

                        await logChangeHotelInfo(logOptions);
                    } catch (logError) {
                        console.error('操作日志上报失败:', logError);
                        // 日志上报失败不影响主流程
                    }
                }
            }
        } catch (error) {
            console.log('保存失败:', error);
        }
    };

    // 处理住宿需求变更
    const handleNeedHotelChange = value => {
        setNeedHotel(value);
    };

    // 处理日期变化 - 筛选可用酒店
    const handleDateChange = (date, dateType) => {
        const newDateRange = {
            ...selectedDateRange,
            [dateType]: date,
        };
        setSelectedDateRange(newDateRange);

        // 如果入住和退房日期都已选择，则筛选酒店
        if (newDateRange.checkin && newDateRange.checkout) {
            filterHotelsByDateRange(newDateRange.checkin, newDateRange.checkout);
        } else {
            // 如果日期不完整，重置筛选
            setFilteredHotelOptions(hotelOptions);
        }

        // 清空已选择的酒店和房型
        form.setFieldsValue({
            hotel_id: undefined,
            hotel_room_type_id: undefined,
        });
        setSelectedHotelId(null);
        setHotelRoomTypeOptions([]);
    };

    // 根据日期范围筛选酒店
    const filterHotelsByDateRange = (checkinDate, checkoutDate) => {
        if (!checkinDate || !checkoutDate) {
            setFilteredHotelOptions(hotelOptions);
            return;
        }

        const dates = generateDateRange(checkinDate, checkoutDate);

        // 筛选在所有日期都有房间的酒店
        const filtered = hotelOptions.filter(hotel => {
            if (!hotel.roomTypeDays || hotel.roomTypeDays.length === 0) {
                return false;
            }

            // 检查酒店是否在所有所需日期都有房型数据
            return dates.every(date => {
                const dayData = hotel.roomTypeDays.find(d => d.day === date);
                return dayData && dayData.roomInfo && dayData.roomInfo.length > 0;
            });
        });

        setFilteredHotelOptions(filtered);
    };

    // 处理酒店选择变化 - 根据日期筛选该酒店下的房型
    const handleHotelChange = hotelId => {
        setSelectedHotelId(hotelId);

        if (hotelId) {
            const selectedHotel = hotelOptions.find(h => h.value === hotelId);
            if (selectedHotel && selectedHotel.roomTypes) {
                // 根据日期范围设置房型选项，并标记是否禁用
                const roomTypesToSet = convertRoomTypesToOptions(selectedHotel.roomTypes, selectedHotel, selectedDateRange.checkin, selectedDateRange.checkout);
                setHotelRoomTypeOptions(roomTypesToSet);
            } else {
                setHotelRoomTypeOptions([]);
            }
        } else {
            setHotelRoomTypeOptions([]);
            form.setFieldsValue({ hotel_room_type_id: undefined });
        }
    };

    // 计算天数
    const calculateNights = () => {
        const checkInDate = form.getFieldValue('checkin_date');
        const checkOutDate = form.getFieldValue('checkout_date');

        if (checkInDate && checkOutDate) {
            return checkOutDate.diff(checkInDate, 'days');
        }

        return 0;
    };

    return (
        <Modal
            title="编辑酒店住宿"
            open={open}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    取 消
                </Button>,
                <Button key="submit" type="primary" onClick={handleSubmit}>
                    提 交
                </Button>,
            ]}
            width={600}
            className="hotel-edit-modal"
        >
            {/* 嘉宾基本信息 */}
            {guestInfo && (
                <div className="guest-basic-info">
                    <div className="info-item">
                        <span className="label">姓名：</span>
                        <span className="value">
                            {guestInfo.name || '–'}
                            {(guestInfo.gender === 0 || guestInfo.gender === 1) && <i className={`bi bi-person-fill gender-icon ${guestInfo.gender === 0 ? 'female' : 'male'}`}></i>}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label">级别：</span>
                        <span className="value line-level">{guestInfo.levelLabel || '–'}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">单位：</span>
                        <span className="value">
                            <WorkExperiences workExperiences={guestInfo.workExperiences} layout="inline" showSerialNumber={false} emptyText="–" />
                        </span>
                    </div>
                </div>
            )}

            <div className="modal-content">
                {/* 系统建议日期 */}
                {(guestInfo?.suggestedCheckIn || guestInfo?.suggestedCheckOut) && (
                    <div className="suggested-date-section">
                        <span className="system-tag">系统</span>
                        <div>
                            {guestInfo?.suggestedCheckIn && <span className="date-text">建议入住日期 {guestInfo.suggestedCheckIn}</span>}
                            {guestInfo?.suggestedCheckOut && <span className="date-text">建议退房日期 {guestInfo.suggestedCheckOut}</span>}
                        </div>
                    </div>
                )}

                {/* 表单内容 */}
                <Form form={form} className="hotel-form" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
                    {/* 住宿需求 */}
                    <Form.Item label="住宿需求" name="need">
                        <Radio.Group onChange={e => handleNeedHotelChange(e.target.value)}>
                            <Radio value={1}>会务组安排</Radio>
                            <Radio value={2}>自费</Radio>
                            <Radio value={0}>不需要</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* 住宿信息表单（仅在选择"会务组安排"或"自费"时显示） */}
                    {!!needHotel && (
                        <>
                            {/* 入住-退房日期 */}
                            <Form.Item label="入住-退房" extra={calculateNights() > 0 ? `${calculateNights()}间夜` : null}>
                                <div className="date-range-wrapper">
                                    <Form.Item name="checkin_date" noStyle>
                                        <DatePicker placeholder="入住日期" format="YYYY-MM-DD" className="date-picker" onChange={date => handleDateChange(date, 'checkin')} disabledDate={current => current && current < dayjs().startOf('day')} />
                                    </Form.Item>
                                    <span className="separator"> - </span>
                                    <Form.Item name="checkout_date" noStyle>
                                        <DatePicker placeholder="退房日期" format="YYYY-MM-DD" className="date-picker" onChange={date => handleDateChange(date, 'checkout')} disabledDate={current => current && current < dayjs().startOf('day')} />
                                    </Form.Item>
                                </div>
                            </Form.Item>
                            {/* 入住酒店 */}
                            <Form.Item label="入住酒店" name="hotel_id">
                                <Select
                                    placeholder={selectedDateRange.checkin && selectedDateRange.checkout ? '请选择入住酒店' : '请先选择入住-退房日期'}
                                    options={filteredHotelOptions.length > 0 ? filteredHotelOptions : hotelOptions}
                                    onChange={handleHotelChange}
                                    allowClear
                                    showSearch={{ optionFilterProp: 'label' }}
                                    disabled={!selectedDateRange.checkin || !selectedDateRange.checkout}
                                />
                            </Form.Item>
                            {/* 安排房型 */}
                            <Form.Item label="安排房型" name="hotel_room_type_id">
                                <Select placeholder="请先选择酒店" options={hotelRoomTypeOptions} disabled={!selectedHotelId} allowClear showSearch={{ optionFilterProp: 'label' }} />
                            </Form.Item>
                            {/* 房间号 */}
                            <Form.Item label="房间号" name="room_no">
                                <Input placeholder="请输入房间号" />
                            </Form.Item>
                        </>
                    )}

                    {/* 备注 - 移到表单最后 */}
                    <div className="remarks-section">
                        <Form.Item label="住宿备注" style={{ marginBottom: 0 }}>
                            <Form.Item name="remarks" noStyle>
                                <TextArea placeholder="填写住宿备注信息" rows={3} />
                            </Form.Item>
                            <RemarksTip />
                        </Form.Item>
                    </div>
                </Form>
            </div>
        </Modal>
    );
};

export default HotelEditModal;
